/**
 * O que a ficha do foco e a denúncia dizem dos raios antes do foco, a partir
 * da consulta por ponto. Só afirma "nenhum raio" com a janela inteira lida
 * (`COBERTURA_MINIMA`): sem dado não é sem raio.
 */
import { formatInt } from '../lib/format';
import { formatDateTimeBrt } from '../lib/time';
import { formatDecimal } from '../fires/format';
import type { RaiosPerto } from './api';

/** Fração da janela que precisa ter sido lida para afirmar algo (a NOAA às vezes perde um arquivo de 20 s). */
export const COBERTURA_MINIMA = 0.95;

export type SituacaoRaios = 'nenhum' | 'houve' | 'incompleto';

export function situacaoRaios(r: RaiosPerto): SituacaoRaios {
  if (r.n > 0) return 'houve';
  return r.cobertura >= COBERTURA_MINIMA ? 'nenhum' : 'incompleto';
}

const km = (n: number) => (n < 1 ? 'menos de 1 km' : `${formatDecimal(n, n < 10 ? 1 : 0)} km`);

/** Uma linha curta (ficha do foco e fatos da denúncia). */
export function fraseCurta(r: RaiosPerto): string {
  const raio = `${formatInt(r.raioKm)} km`;
  switch (situacaoRaios(r)) {
    case 'nenhum':
      return `Nenhum raio a até ${raio} nas ${formatInt(r.horas)} h antes`;
    case 'houve':
      return `${formatInt(r.n)} ${r.n === 1 ? 'raio' : 'raios'} a até ${raio} nas ${formatInt(r.horas)} h antes${r.maisPertoKm != null ? ` (o mais perto a ${km(r.maisPertoKm)})` : ''}`;
    case 'incompleto':
      return `Raios: sem dado completo das ${formatInt(r.horas)} h antes`;
  }
}

/** A linha curta da denúncia: o texto que vai ao órgão diz "descarga atmosférica", não "raio" (pedido do usuário, 29/10/2026). */
export function fraseDescargas(r: RaiosPerto): string {
  const ate = `a até ${formatInt(r.raioKm)} km nas ${formatInt(r.horas)} h antes`;
  switch (situacaoRaios(r)) {
    case 'nenhum':
      return `Nenhuma descarga atmosférica ${ate}`;
    case 'houve':
      return `${formatInt(r.n)} ${r.n === 1 ? 'descarga atmosférica' : 'descargas atmosféricas'} ${ate}${r.maisPertoKm != null ? ` (a mais perto a ${km(r.maisPertoKm)})` : ''}`;
    case 'incompleto':
      return `Descargas atmosféricas: sem dado completo das ${formatInt(r.horas)} h antes`;
  }
}

/** A linha do texto da denúncia (null quando não há o que afirmar). */
export function fraseDaDenuncia(r: RaiosPerto): string | null {
  const janela = `a até ${formatInt(r.raioKm)} km do foco nas ${formatInt(r.horas)} horas anteriores à detecção`;
  switch (situacaoRaios(r)) {
    case 'nenhum':
      return (
        `- Descargas atmosféricas: o satélite GOES-19 (mapeador GLM, da NOAA) não registrou nenhuma descarga atmosférica ${janela}.`
      );
    case 'houve':
      return (
        `- Descargas atmosféricas: o satélite GOES-19 (mapeador GLM, da NOAA) registrou ${formatInt(r.n)} ${r.n === 1 ? 'descarga atmosférica' : 'descargas atmosféricas'} ${janela}` +
        `${r.maisPertoKm != null ? `; a mais próxima a ${km(r.maisPertoKm)}` : ''}${r.ultimo != null ? `, a mais recente em ${formatDateTimeBrt(r.ultimo)} (Brasília)` : ''}.`
      );
    case 'incompleto':
      return null;
  }
}
